/**
 * File: SummaryLine.tsx
 * Path: apps/admin/src/shared/components/SummaryLine.tsx
 * Description: The one line of figures a list page opens with (Pipeline,
 * Clients, Projects, Care plans; admin UI audit C8). Each figure is a label,
 * the amount, an optional status chip and an (i) with what it means. It wraps
 * on a phone instead of scrolling sideways.
 */

import React from "react";
import { AmountText, StatusChip, labelTextClass, type ChipTone } from "@/shared/components/TextPrimitive";
import { InfoHint } from "@/shared/components/InfoHint";

export interface SummaryItem {
  label: string;
  value: string;
  /** What the figure means, shown behind its (i). */
  hint?: string;
  chip?: { tone: ChipTone; text: string };
  /** A short note beside the value, e.g. "5 deals". */
  note?: string;
}

export const SummaryLine: React.FC<{ items: readonly SummaryItem[]; label: string; trailing?: React.ReactNode }> = ({ items, label, trailing }) => (
  <dl aria-label={label} className="flex flex-wrap items-center gap-x-gutter-lg gap-y-stack-sm border-y border-border/50 py-stack-md">
    {items.map((item) => (
      <div key={item.label} className="flex flex-wrap items-center gap-x-stack-sm gap-y-stack-xs">
        <dt className={`flex items-center gap-stack-xs ${labelTextClass}`}>
          {item.label}
          {item.hint && <InfoHint name={item.label}>{item.hint}</InfoHint>}
        </dt>
        <dd className="flex flex-wrap items-center gap-stack-xs">
          <AmountText size="key">{item.value}</AmountText>
          {item.note && <span className="text-sm text-muted-foreground">{item.note}</span>}
          {item.chip && <StatusChip tone={item.chip.tone}>{item.chip.text}</StatusChip>}
        </dd>
      </div>
    ))}
    {trailing}
  </dl>
);
